import { useEffect, useState, useCallback } from 'react';

/** P3-A8: the browser only ever fires `beforeinstallprompt` ONCE, and only
 * if nothing calls `.preventDefault()` + stashes it early. This module-level
 * ref is set up at bootstrap (main.tsx, before auth renders) so the event is
 * never missed regardless of which component mounts first; `useInstallPrompt`
 * is just a reactive READ of that stash. */
let stashedEvent: BeforeInstallPromptEvent | null = null;
let listenerRegistered = false;
const subscribers = new Set<() => void>();

export const TASKS_COMPANION_INSTALL_HINT_KEY = 'valentine-tasks:installed';

function notify() {
  subscribers.forEach((cb) => cb());
}

export function recordCompanionInstallHint(): void {
  localStorage.setItem(TASKS_COMPANION_INSTALL_HINT_KEY, 'true');
}

export function registerInstallPromptListener({
  onAppInstalled,
}: { onAppInstalled?: () => void } = {}) {
  if (listenerRegistered) return;
  listenerRegistered = true;
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    stashedEvent = event;
    notify();
  });
  window.addEventListener('appinstalled', () => {
    stashedEvent = null;
    onAppInstalled?.();
    notify();
  });
}

export function useInstallPrompt() {
  const [, forceRender] = useState(0);

  useEffect(() => {
    const cb = () => forceRender((n) => n + 1);
    subscribers.add(cb);
    return () => {
      subscribers.delete(cb);
    };
  }, []);

  const promptInstall = useCallback(async (): Promise<'accepted' | 'dismissed' | 'unavailable'> => {
    if (!stashedEvent) return 'unavailable';
    const event = stashedEvent;
    await event.prompt();
    const choice = await event.userChoice;
    stashedEvent = null;
    notify();
    return choice.outcome;
  }, []);

  return { canInstall: stashedEvent !== null, promptInstall };
}
